import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./app-dialog.css";

// In-app replacements for window.confirm / window.alert, drawn as an iOS-style
// action sheet so they match the rest of the app. Call showConfirm/showAlert
// from anywhere; <AppDialogHost /> (mounted once in index.js) renders them.

let enqueue = null;

export function showConfirm({
  title,
  message = "",
  confirmLabel = "OK",
  cancelLabel = "Cancel",
  destructive = false,
} = {}) {
  if (!enqueue) return Promise.resolve(window.confirm([title, message].filter(Boolean).join("\n\n")));
  return new Promise((resolve) => {
    enqueue({ kind: "confirm", title, message, confirmLabel, cancelLabel, destructive, resolve });
  });
}

export function showAlert(message, { title = "", buttonLabel = "OK" } = {}) {
  if (!enqueue) {
    window.alert(message);
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    enqueue({ kind: "alert", title, message, confirmLabel: buttonLabel, resolve: () => resolve() });
  });
}

export function AppDialogHost() {
  const [queue, setQueue] = useState([]);
  const current = queue[0] || null;
  const primaryRef = useRef(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    enqueue = (item) => setQueue((q) => [...q, item]);
    return () => { enqueue = null; };
  }, []);

  const close = (result) => {
    if (!current) return;
    current.resolve(result);
    setQueue((q) => q.slice(1));
  };

  useEffect(() => {
    if (!current) return undefined;
    // Destructive: land on Cancel so a stray Enter doesn't delete anything.
    const target = current.kind === "confirm" && current.destructive ? cancelRef.current : primaryRef.current;
    target?.focus({ preventScroll: true });
    const onKey = (e) => { if (e.key === "Escape") close(current.kind === "alert" ? undefined : false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  if (!current) return null;

  const isConfirm = current.kind === "confirm";
  const heading = current.title || current.message;
  const body = current.title ? current.message : "";

  return createPortal(
    <div className="app-dialog-overlay" onClick={() => close(isConfirm ? false : undefined)}>
      <div
        className="app-dialog"
        role={isConfirm ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-dialog-group">
          <div className="app-dialog-text">
            <p id="app-dialog-title" className="app-dialog-title">{heading}</p>
            {body && <p className="app-dialog-message">{body}</p>}
          </div>
          <button
            ref={primaryRef}
            type="button"
            className={`app-dialog-btn${isConfirm && current.destructive ? " app-dialog-btn--destructive" : ""}`}
            onClick={() => close(isConfirm ? true : undefined)}
          >
            {current.confirmLabel}
          </button>
        </div>
        {isConfirm && (
          <button
            ref={cancelRef}
            type="button"
            className="app-dialog-btn app-dialog-btn--cancel"
            onClick={() => close(false)}
          >
            {current.cancelLabel}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
